import './ReportFull.scss';

import {
  faArrowDown,
  faArrowUp,
  faClock,
  faCoins,
  faPercent,
  faUsers,
  faWallet,
} from '@fortawesome/free-solid-svg-icons';
import { formatDateInEN, formatInReal } from '../../commons/util/HallAppUtil';
import { useEffect, useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import HallAppToast from '../../commons/HallAppToast';
import InformativeModal from '../../commons/modal/InformativeModal';
import MenuTitle from '../../commons/menu/MenuTitle';
import ReportAPI from '../report/ReportAPI';
import ReportFullFilter from './ReportFullFilter';
import ReportHeader from './components/ReportHeader';
import { useNavigate } from 'react-router-dom';

/* eslint-disable react-hooks/exhaustive-deps */
function ReportFull() {
  const [isLoadingSummary, setIsLoadingSummary] = useState(true);
  const [transactionSummary, setTransactionSummary] = useState({});
  const [showInformativeModal, setShowInformativeModal] = useState(false);
  const [informativeType, setInformativeType] = useState(null);

  const reportAPI = new ReportAPI();

  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [filter, setFilter] = useState({
    startDate: formatDateInEN(firstDayOfMonth),
    endDate: formatDateInEN(new Date()),
  });

  useEffect(() => {
    findReportFullSummary();
  }, [filter]);

  async function findReportFullSummary() {
    try {
      setIsLoadingSummary(true);
      const response = await reportAPI.getReportFullSummary(filter);
      setTransactionSummary(response.data || {});
    } catch (_) {
      HallAppToast.genericError();
      setTransactionSummary({});
    } finally {
      setIsLoadingSummary(false);
    }
  }

  // Mantém sua regra: se usuário escolhe apenas um mês (endDate null),
  // ajusta endDate para o último dia do mês+1 (janela de 2 meses).
  function changeDates(startDate, endDate) {

    setFilter({
      startDate,
      endDate: endDate,
    });
  }

  const InfoCard = ({
    icon,
    label,
    value,
    bg,
    onOpenDetails,   // NOVO: clique no card vai pros detalhes
    onOpenInfo,      // NOVO: clique no ícone abre modal
    loading,
    tone = 'dark',
    hint,            // opcional: "Comandas + Entradas externas"
  }) => (
    <button
      type="button"
      onClick={onOpenDetails}
      className={`rf-card rf-card--${tone}`}
      style={{ backgroundColor: bg }}
      aria-label={`${label}: ${loading ? 'Carregando' : value}`}
    >
      <div className="rf-card__body">
        <div className="rf-card__left">
          <div className="rf-card__icon">
            <FontAwesomeIcon icon={icon} size="lg" />
          </div>

          <div className="rf-card__info">
            <div className="rf-card__labelRow">
              <div className="rf-card__label">{label}</div>

              {/* botão info separado (não dispara o click do card) */}
              <button
                type="button"
                className="rf-card__infoBtn"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onOpenInfo?.();
                }}
                aria-label={`Entenda ${label}`}
                title={`Entenda ${label}`}
              >
                ?
              </button>
            </div>

            <div className="rf-card__value">
              {loading ? (
                <span
                  className="rf-card__spinner spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                />
              ) : (
                value
              )}
            </div>

            {/* hint bem curtinho pra explicar sem precisar modal */}
            {hint ? <div className="rf-card__hint">{hint}</div> : null}

            {onOpenDetails && <div className="rf-card__cta">
              <span>Ver detalhes</span>
              <span className="rf-card__chevron">›</span>
            </div>}
          </div>
        </div>
      </div>
    </button>
  );

  const navigate = useNavigate();

  function openInfo(type) {
    setInformativeType(type);
    setShowInformativeModal(true);
  }

  function closeInfo() {
    setShowInformativeModal(false);
    setInformativeType(null);
  }

  function buildInfoByType() {
    if (informativeType === 'TOTAL_GROSS') {
      return (
        <div className="p-3 border rounded bg-light">
          <h6 className="fw-bold">O que é o valor bruto?</h6>
          <p>
            O <strong>valor bruto</strong> representa a <strong>soma total de tudo que foi recebido no dia</strong>,
            antes de qualquer desconto ou comissão. Ele é composto por:
          </p>
          <ul>
            <li>💇‍♀️ <strong>Comandas</strong>: todos os serviços registrados (cortes, tinturas etc.).</li>
            <li>💵 <strong>Entradas externas</strong>: depósitos, pagamentos avulsos e vendas fora de comandas.</li>
          </ul>
          <p>
            Em resumo, <strong>valor bruto</strong> = <strong>Total das comandas + Entradas externas</strong>.
          </p>
        </div>
      );
    } else if (informativeType === 'TOTAL_NET') {
      return (
        <div className="p-3 border rounded bg-light">
          <h6 className="fw-bold">O que é o total líquido?</h6>
          <p>
            O <strong>total líquido</strong> é o <strong>valor que fica para o salão</strong> após descontar pagamentos
            aos profissionais e despesas.
          </p>
          <ul>
            <li>💰 <strong>Valor bruto</strong> (comandas + entradas externas)</li>
            <li>➖ <strong>Comissões</strong> pagas aos profissionais</li>
            <li>➖ <strong>Saídas externas</strong> (luz, água, compras, operacionais)</li>
          </ul>
          <p>
            Em resumo, <strong>total líquido</strong> = <strong>Valor bruto - Comissões - Saídas externas</strong>.
          </p>
        </div>
      );
    } else if (informativeType === 'TOTAL_RECEIVABLE') {
      return (
        <div className="p-3 border rounded bg-light">
          <h6 className="fw-bold">O que são os valores a receber?</h6>
          <p>
            São <strong>comandas não pagas</strong> no período: serviços realizados que ainda aguardam pagamento.
          </p>
          <ul>
            <li>📋 <strong>Comandas pendentes</strong></li>
            <li>⌛ <strong>Status em aberto</strong> (aguardando PIX, cartão, dinheiro, etc.)</li>
          </ul>
          <p>Esses valores não entram no total líquido até serem quitados.</p>
        </div>
      );
    } else if (informativeType === 'TOTAL_COMMISSION') {
      return (
        <div className="p-3 border rounded bg-light">
          <h6 className="fw-bold">O que é o total de comissões?</h6>
          <p>
            Soma das <strong>comissões devidas aos profissionais</strong> pelos serviços realizados no período.
          </p>
          <ul>
            <li>👩‍🔧 Porcentagem aplicada sobre os serviços executados</li>
          </ul>
          <p>É deduzido do valor bruto para chegar ao total líquido.</p>
        </div>
      );
    } else if (informativeType === 'TOTAL_OUTPUT') {
      return (
        <div className="p-3 border rounded bg-light">
          <h6 className="fw-bold">O que são os gastos extras?</h6>
          <p>
            Despesas do dia a dia não ligadas diretamente às comandas/comissões.
          </p>
          <ul>
            <li>💡 Fixas: água, luz, internet, aluguel</li>
            <li>🛒 Compras: produtos, limpeza, insumos</li>
            <li>💸 Operacionais: saídas diversas do caixa</li>
          </ul>
          <p>Consideradas no cálculo do total líquido.</p>
        </div>
      );
    } else if (informativeType === 'TOTAL_INPUT') {
      return (
        <div className="p-3 border rounded bg-light">
          <h6 className="fw-bold">O que são as entradas extras?</h6>
          <p>Recebimentos que não estão vinculados diretamente a comandas.</p>
          <ul>
            <li>💳 Pagamentos avulsos</li>
            <li>🏦 Depósitos/transferências para o caixa</li>
            <li>🛍️ Vendas diretas sem comanda</li>
            <li>🏢 Aluguel de salas (parceiros/terceiros)</li>
          </ul>
          <p>Somam no valor bruto do período.</p>
        </div>
      );
    } else if (informativeType === 'CUSTOMER') {
      return (
        <div className="p-3 border rounded bg-light">
          <h6 className="fw-bold">O que representa a quantidade de clientes?</h6>
          <p>
            Total de <strong>atendimentos/comandas</strong> no período. Clientes com múltiplas comandas contam mais de uma vez.
          </p>
          <ul>
            <li>👥 Indicador de movimento/fluxo do salão</li>
            <li>📊 Útil para avaliar produtividade do time</li>
          </ul>
        </div>
      );
    } else if (informativeType === 'TOTAL_FEE') {
      return (
        <div className="p-3 border rounded bg-light">
          <h6 className="fw-bold">O que representam as taxas?</h6>
          <p>
            Custos cobrados por operadoras ao receber via <strong>crédito</strong>, <strong>débito</strong> ou <strong>Pix</strong> (maquininha).
          </p>
          <ul>
            <li>💳 Crédito: percentual por transação (varia com parcelas)</li>
            <li>🏦 Débito: taxa fixa ou percentual</li>
            <li>⚡ Pix maquininha: pode ter taxa reduzida</li>
          </ul>
          <p>Impactam o <strong>lucro líquido</strong> real.</p>
        </div>
      );
    }
    return null;
  }

  return (
    <div className="report-full">
      <ReportHeader name="Relatório Geral" />

      <div className="report-full__filter">
        <ReportFullFilter changeDates={changeDates} />
      </div>

      <div className="container py-5">
        <MenuTitle title="Resumo Financeiro" />

        {/* Resumo principal */}
        <div className="row g-4 mb-5">
          <div className="col-md-6 col-xl-4">
            <InfoCard
              onOpenDetails={() => {
                navigate('/relatorio-geral/gross-detail')
              }}
              onOpenInfo={() => openInfo('TOTAL_GROSS')}
              label="Total Bruto"
              value={formatInReal(transactionSummary?.totalGross || 0)}
              icon={faCoins}
              bg="#343a40"
              loading={isLoadingSummary}
              tone="dark"
              hint="Comandas + Entradas externas"
            />
          </div>

          <div className="col-md-6 col-xl-4">
            <InfoCard
              onOpenInfo={() => openInfo('TOTAL_RECEIVABLE')}
              label="À Receber"
              value={formatInReal(transactionSummary?.totalReceivable || 0)}
              icon={faClock}
              bg="#f0ad4e"
              onOpenDetails={() => {
                navigate(`/relatorio?status=WAITING_FOR_PAYMENT&startDate=${filter.startDate}&endDate=${filter.endDate}`)
              }}
              loading={isLoadingSummary}
              tone="dark"
            />
          </div>

          <div className="col-md-6 col-xl-4">
            <InfoCard
              onOpenInfo={() => openInfo('TOTAL_NET')}
              label="Total Líquido"
              value={formatInReal(transactionSummary?.totalNet || 0)}
              icon={faWallet}
              bg="#198754"
              loading={isLoadingSummary}
              tone="dark"
            />
          </div>
        </div>

        <hr className="mb-5" />

        <h4 className="fw-semibold mb-4 text-muted">Outros Dados</h4>

        <div className="row g-4 mb-5">
          <div className="col-md-6 col-xl-4">
            <InfoCard
              onOpenInfo={() => openInfo('TOTAL_FEE')}
              label="Taxas"
              value={formatInReal(transactionSummary?.totalFee || 0)}
              icon={faArrowDown}
              bg="#e66b77"
              loading={isLoadingSummary}
              tone="dark"
            />
          </div>
          <div className="col-md-6 col-xl-4">
            <InfoCard
              onOpenInfo={() => openInfo('TOTAL_COMMISSION')}
              label="Comissão"
              value={formatInReal(transactionSummary?.totalCommission || 0)}
              onOpenDetails={() => {
                navigate(`/fechamento-caixa?startDate=${filter.startDate}&endDate=${filter.endDate}`)
              }}
              icon={faPercent}
              bg="#dc3545"
              loading={isLoadingSummary}
              tone="dark"
            />
          </div>
          <div className="col-md-6 col-xl-4">
            <InfoCard
              onOpenInfo={() => openInfo('TOTAL_OUTPUT')}
              onOpenDetails={() => {
                navigate(`/external-transactions?status=OUTPUT&startDate=${filter.startDate}&endDate=${filter.endDate}`)
              }}
              label="Gastos Extras"
              value={formatInReal(transactionSummary?.totalOutput || 0)}
              icon={faArrowDown}
              bg="#fd7e14"
              loading={isLoadingSummary}
              tone="dark"
            />
          </div>
          <div className="col-md-6 col-xl-4">
            <InfoCard
              onOpenInfo={() => openInfo('TOTAL_INPUT')}
              onOpenDetails={() => {
                navigate(`/external-transactions?status=INPUT&startDate=${filter.startDate}&endDate=${filter.endDate}`)
              }}
              label="Entradas Extras"
              value={formatInReal(transactionSummary?.totalInput || 0)}
              icon={faArrowUp}
              bg="#0d6efd"
              loading={isLoadingSummary}
              tone="dark"
            />
          </div>

          <div className="col-md-6 col-xl-4">
            <InfoCard

              label="Club de Assinatura"
              value={formatInReal(transactionSummary?.totalClub || 0)}
              icon={faArrowUp}
              bg="#0d6efd"
              loading={isLoadingSummary}
              tone="dark"
            />
          </div>

          <div className="col-md-6 col-xl-4">
            <InfoCard

              label="Pacotes"
              value={formatInReal(transactionSummary?.totalPackageSales || 0)}
              icon={faArrowUp}
              bg="#0d6efd"
              loading={isLoadingSummary}
              tone="dark"
            />
          </div>
        </div>

        <div className="row g-4">
          <div className="col-12 col-md-6 col-lg-4">
            <InfoCard
              onOpenInfo={() => openInfo('CUSTOMER')}
              onOpenDetails={() => {
                navigate('/relatorio-geral/visit-detail')
              }}
              label="Clientes Atendidos"
              value={transactionSummary?.totalCustomer || 0}
              icon={faUsers}
              bg="#6f42c1"
              loading={isLoadingSummary}
              tone="dark"
            />
          </div>
        </div>
      </div>
      <div style={{ height: "calc(50px + 72px)" }}></div>
      {showInformativeModal && (
        <InformativeModal
          show={showInformativeModal}
          closeModal={closeInfo}
        >
          {buildInfoByType()}
        </InformativeModal>
      )}
    </div>
  );
}

export default ReportFull;